import { lazy, Suspense } from "react";
import { Eyebrow, Reveal } from "@/components/site/motion";

// The 3D scene (three.js + react-three-fiber) is large, so it only downloads
// when someone actually reaches the Solutions page.
const StorefrontScrollytelling = lazy(() => import("@/components/solutions/StorefrontScrollytelling"));

const DARK = "#0a0a0a";

const loadingStyle = {
  height: "calc(100dvh - 80px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#8a8a8a",
  fontSize: ".85rem",
  letterSpacing: ".1em",
  textTransform: "uppercase",
  borderTop: "1px solid #262626",
  borderBottom: "1px solid #262626",
};

const FEATURES = [
  {
    num: "01",
    title: "Foot traffic, counted",
    body: "Every pass, every walk-in, every person who looked but kept going — turned into a number, without a single new sensor on site.",
  },
  {
    num: "02",
    title: "Attention & dwell time",
    body: "How long someone actually stood at a shelf, a screen or a display — not just that they were in the building.",
  },
  {
    num: "03",
    title: "Zone-level heat",
    body: "Which parts of the room earn attention and which are dead space, mapped across the whole floor in real time.",
  },
];

// Always dark (hard-coded rather than the site's theme "ink"), because the
// 3D scene is lit and designed for a dark page in both light and dark mode.
export default function HapoIntelligenceStorefront() {
  return (
    <section
      aria-label="Hapo Intelligence in a store"
      data-testid="solutions-storefront"
      style={{ background: DARK, color: "#ffffff" }}
    >
      <div className="px-6 pb-16 pt-24 lg:px-12 lg:pt-[140px]">
        <Eyebrow>Hapo Intelligence · Attention Intelligence</Eyebrow>
        <Reveal>
          <h2 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight tracking-tighter sm:text-5xl">
            The cameras are already there. Now they tell you what they see.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-xl text-sm leading-loose md:text-base" style={{ color: "#8a8a8a" }}>
            Hapo Cloud&apos;s Vision AI turns the cameras already covering your store, screen or venue into
            anonymous, real-time measurement — who walked in, where they went, what held their attention.
            Processed on site. Nothing personal stored.
          </p>
        </Reveal>
      </div>

      <Suspense fallback={<div style={loadingStyle}>Loading the storefront…</div>}>
        <StorefrontScrollytelling />
      </Suspense>

      <div className="grid gap-10 px-6 py-24 lg:grid-cols-3 lg:gap-12 lg:px-12 lg:py-[120px]">
        {FEATURES.map((f) => (
          <Reveal key={f.num}>
            <p className="text-[11px] font-bold tracking-[0.18em]" style={{ color: "#545454" }}>
              {f.num}
            </p>
            <h3 className="mt-3 text-lg font-bold">{f.title}</h3>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: "#8a8a8a" }}>
              {f.body}
            </p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
